import compact from 'lodash/compact'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import moment from 'moment-timezone'
import {
  DedicatedLanesBid,
  IUserMotorCarrier,
  BookableShipmentSearchQueryParams,
} from 'apps/fleet-planner/types'
import { DedicatedLane } from 'apps/fleet-planner/types/apiTypes'
import {
  DedicatedLanesFilters,
  DedicatedLanesResponse,
} from 'apps/marketplace/apiTypes/dedicatedLanes'
import formatCurrency from 'utils/formatCurrency'
import { IMarketplaceParamsState } from 'utils/hooks'

export const getDedicatedLanesSearchFiltersFromMarketplace = (
  params: IMarketplaceParamsState
): DedicatedLanesFilters => {
  const { filters } = params || {}
  const {
    deliveryCity,
    deliveryMiles,
    deliveryStateCode,
    equipment,
    pickupCity,
    pickupMiles,
    pickupStateCode,
  } = filters || {}
  const { id: equipmentId } = equipment || {}
  return {
    pickupCity,
    pickupStateCode,
    deliveryCity,
    deliveryStateCode,
    pickupMiles: pickupMiles ? Number(pickupMiles) : undefined,
    deliveryMiles: deliveryMiles ? Number(deliveryMiles) : undefined,
    equipment: { id: equipmentId },
  }
}

export const getDedicatedLanesSearchFiltersFromSavedSeaches = (
  params: BookableShipmentSearchQueryParams
): DedicatedLanesFilters => {
  const { filters } = params || {}
  const {
    deliveryCity,
    deliveryMiles,
    deliveryStateCode,
    pickupCity,
    pickupMiles,
    pickupStateCode,
    trailerType,
  } = filters || {}
  return {
    pickupCity,
    pickupStateCode,
    deliveryCity,
    deliveryStateCode,
    pickupMiles: pickupMiles ? Number(pickupMiles) : undefined,
    deliveryMiles: deliveryMiles ? Number(deliveryMiles) : undefined,
    trailerType,
  }
}

export const formatBidText = (bid: DedicatedLanesBid): string => {
  const { rateAmount, rateType } = bid || {}
  const rate = formatCurrency(rateAmount)
  switch (rateType) {
    /* RPM */
    case 'rfp_lane_carrier_line_haul_rpm': {
      return `${rate}/mi`
    }
    /* Line Haul */
    case 'rfp_lane_carrier_all_in': {
      return `${rate} All In`
    }
    /* Flat */
    case 'rfp_lane_carrier_line_haul_flat': {
      return `${rate} Line Haul`
    }
    default:
      return rate
  }
}

export const formatOfferRowBidText = (bids: DedicatedLanesBid[]) => {
  if (!bids?.length) return ''
  if (bids.length === 1) {
    const [bid] = bids || []
    const { rateAmount, rateType } = bid || {}
    const rate = formatCurrency(rateAmount)
    return rateType === 'rfp_lane_carrier_line_haul_rpm' ? `${rate}/mi bid` : `${rate} bid`
  }
  return `You placed ${bids.length} bids`
}

export const getLoadingMethods = (
  lane: DedicatedLanesResponse
): { pickupMethod: string; deliveryMethod: string } => {
  const { loadingMethod } = lane || {}
  switch (loadingMethod) {
    case 'drop-drop': {
      return { pickupMethod: 'Drop trailer at pickup', deliveryMethod: 'Drop trailer at delivery' }
    }
    case 'drop-live': {
      return { pickupMethod: 'Drop trailer at pickup', deliveryMethod: 'Live unload at delivery' }
    }
    case 'live-drop': {
      return { pickupMethod: 'Live load at pickup', deliveryMethod: 'Drop trailer at delivery' }
    }
    case 'live-live': {
      return { pickupMethod: 'Live load at pickup', deliveryMethod: 'Live unload at delivery' }
    }
    default:
      return { pickupMethod: '', deliveryMethod: '' }
  }
}

/**
 * Calculate startDate and endDate and returns the number of months
 */
export const getMonthDuration = (lane: DedicatedLanesResponse): number => {
  const { endDate = '', startDate = '' } = lane || {}
  const startAt = moment(startDate).format()
  const endAt = endDate ? moment(endDate).format() : ''
  return moment(endAt).diff(moment(startAt), 'months')
}

/**
 * Returns the bid with the largest bidId
 */
export const getMostRecentLaneBid = (bids: DedicatedLanesBid[]): DedicatedLanesBid | undefined => {
  const maxId = Math.max(...bids.map(bid => bid?.bidId || 0))
  return bids.find(bid => bid?.bidId === maxId) || undefined
}

/**
 * Returns an array of the most recent bid for each carrier in userMotorCarriers.
 */
export const getLaneBids = (
  bids: DedicatedLanesBid[],
  shipperRateId: DedicatedLanesResponse['id'],
  userMotorCarriers: IUserMotorCarrier[]
): DedicatedLanesBid[] => {
  return uniq(
    compact(
      map(userMotorCarriers, ({ motorCarrier = {} }) => {
        const { dotNumber, mcNumber } = motorCarrier || {}
        const motorCarrierBids =
          bids.filter(
            bid =>
              bid?.shipperRateId === shipperRateId &&
              (bid?.mcNumber === mcNumber || bid?.dotNumber === dotNumber)
          ) || {}
        return getMostRecentLaneBid(motorCarrierBids)
      })
    )
  )
}

/**
 * Validate the ExpirationDate to show a Expiring Soon tag
 */
export const shouldShowExpirationTag = (dedicatedLane: DedicatedLane): boolean => {
  if (dedicatedLane.expirationDate) {
    const current = moment().startOf('day')
    const days = moment.duration(moment(dedicatedLane.expirationDate).diff(current)).asDays()
    return days < 7
  }

  return false
}
